import { type JSX,useEffect, useRef, useState } from 'react';

const ONLINE_TOAST_AUTO_DISMISS_MS = 4000;

type ToastState = 'offline' | 'online' | null;

/**
 * Mounted once at the app root (see App.tsx) so connectivity changes are announced
 * on every page as a floating toast, without any page needing to know about it.
 * "Offline" persists until the browser reports it's back; "back online" auto-dismisses.
 * The `navigator.onLine` value at mount is never announced — only transitions after
 * mount are, so a page that simply loaded online doesn't show an unprompted toast.
 */
export function NetworkStatusToast(): JSX.Element | null {
  const [toast, setToast] = useState<ToastState>(null);
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef<T>() overload requires the argument
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    function handleOffline(): void {
      clearTimeout(dismissTimeoutRef.current);
      setToast('offline');
    }

    function handleOnline(): void {
      clearTimeout(dismissTimeoutRef.current);
      setToast('online');
      dismissTimeoutRef.current = setTimeout(() => {
        setToast(null);
      }, ONLINE_TOAST_AUTO_DISMISS_MS);
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      clearTimeout(dismissTimeoutRef.current);
    };
  }, []);

  if (!toast) {
    return null;
  }

  const isOffline = toast === 'offline';

  return (
    <div
      aria-live={isOffline ? 'assertive' : 'polite'}
      role={isOffline ? 'alert' : 'status'}
      className="position-fixed bottom-0 start-50 translate-middle-x mb-4"
      style={{ zIndex: 1080 }}
    >
      <div
        className={`d-flex align-items-center gap-2 rounded-pill px-3 py-2 text-white shadow ${
          isOffline ? 'bg-dark' : 'bg-success'
        }`}
      >
        {isOffline ? (
          <svg aria-hidden="true" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="flex-shrink-0">
            <path d="M10.706 3.294A12.6 12.6 0 0 0 8 3C5.259 3 2.723 3.882.663 5.379a.485.485 0 0 0-.048.736.52.52 0 0 0 .668.05A11.45 11.45 0 0 1 8 4q.946 0 1.852.148zM8 6c-1.905 0-3.68.56-5.166 1.526a.48.48 0 0 0-.063.745.525.525 0 0 0 .652.065 8.45 8.45 0 0 1 3.51-1.27zm2.596 1.404.785-.785q.947.362 1.785.907a.482.482 0 0 1 .063.745.525.525 0 0 1-.652.065 8.5 8.5 0 0 0-1.98-.932zM8 10l.933-.933a6.5 6.5 0 0 1 2.013.637c.285.145.326.524.1.75l-.015.015a.53.53 0 0 1-.611.09A5.5 5.5 0 0 0 8 10m4.905-4.905.747-.747q.886.451 1.685 1.03a.485.485 0 0 1 .047.737.52.52 0 0 1-.668.05 11.5 11.5 0 0 0-1.811-1.07M9.02 11.78c.238.14.236.464.04.66l-.707.706a.5.5 0 0 1-.707 0l-.707-.707c-.195-.195-.197-.518.04-.66A2 2 0 0 1 8 11.5c.374 0 .723.102 1.021.28zm4.355-9.905a.53.53 0 0 1 .75.75l-10.75 10.75a.53.53 0 0 1-.75-.75z" />
          </svg>
        ) : (
          <svg aria-hidden="true" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="flex-shrink-0">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
          </svg>
        )}
        <span className="small fw-medium">{isOffline ? "You're offline" : "You're back online"}</span>
      </div>
    </div>
  );
}
